"use client"

import { Children, isValidElement, useEffect, useLayoutEffect, useSyncExternalStore, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence } from "framer-motion"

import { Pop } from "@/components/ui/motion"
import { chromeRow } from "@/lib/fade"
import { CHROME_ROW } from "@/lib/motion"

export const PAGE_HEADER_SLOT_ID = "page-header-slot"
export const PAGE_HEADER_ROW_SLOT_ID = "page-header-row-slot"
export const PAGE_FOOTER_SLOT_ID = "page-footer-slot"

/**
 * The two places a page can contribute controls to the app shell: the header, and the footer.
 *
 * A portal rather than props, because the layout renders one shell for every route and cannot
 * know which page is below it — Template's `topMenu` prop was passed the same value by every
 * call site for exactly that reason, so no page could use it. The shell renders empty slots and
 * whichever page is mounted fills them; controls unmount with the page, so nothing cleans up.
 *
 * A portal rather than a context holding a ReactNode, because that would mean a child setting
 * parent state during render — the cascading-render pattern React's own lint rule rejects, and
 * which has bitten this page before.
 *
 * The footer is the one difference between the ends: the header is always there, while the
 * footer only EXISTS when a page puts something in it. Nothing here decides that — the shell
 * hides an empty footer with `:empty` and lays the page out around it with `:has()`, so the
 * appearing and the fade beneath it are both free (see AppFooter and AppShell).
 */
/*
  The slots are DOM nodes the shell owns — an external store as far as React is concerned, read
  with useSyncExternalStore rather than an effect so this stays out of the cascading-render pattern
  (setState inside useEffect) that React's lint rule rejects. The server snapshot is null, and the
  client re-reads once hydrated.

  The shell registers each node through `slotRef` as it mounts and unmounts, and that is what a
  page's portal subscribes to. This used to subscribe to nothing, on the reasoning that the slots
  never come and go while a page is mounted — which holds until the shell re-renders the element
  around a slot: the header wrapping its slot in a scroller, or Fast Refresh remounting it. The
  node is then replaced, the portal goes on rendering into the old one, detached from the page,
  and every control the page put in the header silently disappears.
*/
const slotNodes = new Map<string, HTMLElement>()
const slotListeners = new Set<() => void>()
const slotRefs = new Map<string, (node: HTMLElement | null) => void>()

const subscribe = (listener: () => void) => {
  slotListeners.add(listener)
  return () => {
    slotListeners.delete(listener)
  }
}
const getServerSnapshot = (): HTMLElement | null => null

/**
 * The ref the shell puts on a slot's node. One stable callback per slot, so React does not detach
 * and re-attach it on every render.
 */
export function slotRef(id: string): (node: HTMLElement | null) => void {
  let ref = slotRefs.get(id)
  if (!ref) {
    ref = (node) => {
      if (node) slotNodes.set(id, node)
      else slotNodes.delete(id)
      slotListeners.forEach((listener) => listener())
    }
    slotRefs.set(id, ref)
  }
  return ref
}

function useSlot(id: string) {
  return useSyncExternalStore(
    subscribe,
    // The registered node, or the one in the document for a slot rendered before registration.
    () => slotNodes.get(id) ?? document.getElementById(id),
    getServerSnapshot,
  )
}

function Slot({ id, children }: { id: string; children: ReactNode }) {
  const target = useSlot(id)
  if (!target) return null
  return createPortal(children, target)
}

/** Renders its children into the app header. */
export function PageHeaderSlot({ children }: { children: ReactNode }) {
  return <Slot id={PAGE_HEADER_SLOT_ID}>{children}</Slot>
}

/**
 * A second row of controls in the app header, open while `open` is true.
 *
 * Opening it grows the header over the page (see `AppHeader`) and, as it grows, each direct child
 * pops in, left to right. Closing takes the controls away at once and the header
 * contracts behind them. Each child is one item of that cascade, so pass a group that should
 * arrive together as one element.
 *
 * The row belongs to whichever page renders this, and closes when that page unmounts.
 */
export function PageHeaderRow({ open, children }: { open: boolean; children: ReactNode }) {
  // A layout effect, so the header starts growing in the same frame the page asked for it.
  useLayoutEffect(() => {
    chromeRow.set(open)
  }, [open])
  useEffect(() => () => chromeRow.set(false), [])

  return (
    <Slot id={PAGE_HEADER_ROW_SLOT_ID}>
      <AnimatePresence>
        {open &&
          Children.toArray(children).map((child, index) => (
            <Pop
              key={isValidElement(child) && child.key !== null ? child.key : index}
              index={index}
              delay={CHROME_ROW.itemsDelay}
              className="pointer-events-auto flex shrink-0 items-center"
            >
              {child}
            </Pop>
          ))}
      </AnimatePresence>
    </Slot>
  )
}

/**
 * Renders its children into the app footer — and, by filling it, is what makes the footer
 * exist at all. Render nothing here and there is no footer and no space reserved for one.
 */
/**
 * The app's horizontal inset — one left edge for everything on a page.
 *
 * The header set `px-6 md:px-12`, the footer `px-4 md:px-8`, the research column `px-8 lg:px-14`
 * and the follow-up composer `px-6 lg:px-12`: four different left edges, so an answer, the box
 * you revise it in, and the buttons under that box each started at a different place down the
 * same column. Nothing chose those numbers together; they were each chosen alone.
 *
 * Held here because the footer slot is where content meets chrome, and both sides need it.
 */
export const PAGE_INSET = "px-6 md:px-12"

export function PageFooterSlot({ children }: { children: ReactNode }) {
  return <Slot id={PAGE_FOOTER_SLOT_ID}>{children}</Slot>
}
